// Card-payments overview (WN payment monitoring). Shows every cc/PayFort order
// and what was actually captured vs the order total — so you can see at a glance
// who paid, who didn't, and anything that looks off. Gated server-side to
// specific admin users (Mohammad Dev); the nav item is also user-gated.
import React from "react";
import { Link } from "react-router-dom";
import api from "@services/api";

const STATUS = {
  PAID:       { color: "#188038", bg: "#e6f4ea", label: "Paid" },
  PARTIAL:    { color: "#b26a00", bg: "#fef3e0", label: "Partial" },
  UNPAID:     { color: "#c0392b", bg: "#fdecea", label: "Unpaid" },
  OVER:       { color: "#8e44ad", bg: "#f3e8fb", label: "Over-charged" },
  UNVERIFIED: { color: "#5f6368", bg: "#eceff1", label: "Unverified" },
};

const money = (n) => Number(n || 0).toFixed(3);

export default function CcPaymentsPage() {
  const [days, setDays] = React.useState(14);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [filter, setFilter] = React.useState("ALL");

  const load = React.useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get(`/washy/cc-payments?days=${days}`)
      .then((r) => setData(r.data))
      .catch((e) =>
        setError(e?.response?.status === 403 ? "This page isn't available for your account." : "Couldn't load card payments.")
      )
      .finally(() => setLoading(false));
  }, [days]);

  React.useEffect(() => { load(); }, [load]);

  const s = data?.summary || {};
  const orders = (data?.orders || []).filter((o) => filter === "ALL" || o.status === filter);

  const chip = (key, count) => {
    const st = STATUS[key];
    const active = filter === key;
    return (
      <button
        key={key}
        type="button"
        onClick={() => setFilter(active ? "ALL" : key)}
        style={{
          border: active ? `2px solid ${st.color}` : "1px solid #ddd",
          background: active ? st.bg : "#fff", color: st.color,
          borderRadius: 999, padding: "6px 14px", cursor: "pointer", fontWeight: 600, fontSize: 13,
        }}
      >
        {st.label}: {count ?? 0}
      </button>
    );
  };

  return (
    <div style={{ padding: 16, fontFamily: "Inter, system-ui, sans-serif" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>Card Payments</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label style={{ fontSize: 13 }}>Last</label>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #ccc" }}>
            {[3, 7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>{d} days</option>)}
          </select>
          <button type="button" onClick={load} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #ccc", cursor: "pointer" }}>Refresh</button>
        </div>
      </div>

      {error && <p style={{ color: "#c0392b", marginTop: 16 }}>{error}</p>}

      {!error && (
        <>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "16px 0" }}>
            {chip("PAID", s.paid)}
            {chip("PARTIAL", s.partial)}
            {chip("UNPAID", s.unpaid)}
            {chip("OVER", s.over)}
            {chip("UNVERIFIED", s.unverified)}
            <span style={{ marginLeft: "auto", fontSize: 13, color: "#555", alignSelf: "center" }}>
              {data ? `${data.count} orders · total ${money(s.total)} JOD · captured ${money(s.captured)} JOD` : ""}
            </span>
          </div>

          {loading ? (
            <p>Loading…</p>
          ) : (
            <div style={{ overflowX: "auto", border: "1px solid #eee", borderRadius: 10 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5, minWidth: 820 }}>
                <thead>
                  <tr style={{ background: "#fafafa", textAlign: "left" }}>
                    <th style={th}>Order</th>
                    <th style={th}>Customer</th>
                    <th style={th}>Platform</th>
                    <th style={{ ...th, textAlign: "right" }}>Total</th>
                    <th style={{ ...th, textAlign: "right" }}>Captured</th>
                    <th style={{ ...th, textAlign: "right" }}>Remaining</th>
                    <th style={th}>Status</th>
                    <th style={th}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => {
                    const st = STATUS[o.status] || STATUS.UNVERIFIED;
                    return (
                      <tr key={o.order_id} style={{ borderTop: "1px solid #f0f0f0" }}>
                        <td style={td}><Link to={`/orders/${o.order_id}`} style={{ color: "#2563eb" }}>#{o.order_id}</Link></td>
                        <td style={td}>{o.customer}</td>
                        <td style={td}>{o.platform || "-"}</td>
                        <td style={{ ...td, textAlign: "right" }}>{money(o.total)}</td>
                        <td style={{ ...td, textAlign: "right" }}>{money(o.captured)}</td>
                        <td style={{ ...td, textAlign: "right", color: o.remaining > 0.001 ? "#c0392b" : "#188038" }}>{money(o.remaining)}</td>
                        <td style={td}>
                          <span style={{ background: st.bg, color: st.color, borderRadius: 6, padding: "2px 8px", fontWeight: 600, fontSize: 12 }}>{st.label}</span>
                        </td>
                        <td style={{ ...td, whiteSpace: "nowrap", color: "#666" }}>{(o.date_added || "").slice(0, 16)}</td>
                      </tr>
                    );
                  })}
                  {orders.length === 0 && (
                    <tr><td colSpan={8} style={{ ...td, textAlign: "center", color: "#888" }}>No orders.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <p style={{ fontSize: 12, color: "#888", marginTop: 10 }}>
            “Captured” is the real amount PayFort took. <b>Unverified</b> = a card charge exists but its amount couldn’t be read — check it before collecting anything.
          </p>
        </>
      )}
    </div>
  );
}

const th = { padding: "10px 12px", fontWeight: 700, borderBottom: "1px solid #eee" };
const td = { padding: "9px 12px" };
